import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
} from '@/components/ui';
import { graduationYearParam } from '@/features/admin/filters';
import { ACCOUNT_TONES, humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * The student register.
 *
 * `Users` lists accounts and can only ever show a name, a role and a status.
 * A student is a profile: the university, the graduation year and whether they
 * are looking are the things an admin actually searches by.
 */
/**
 * Whether companies can find the student in search and recommendations -- the
 * student's own "Open to opportunities" choice. Same values the dropdown sent.
 */
const LOOKING_TABS = [
  { key: '', label: 'All' },
  { key: 'yes', label: 'Visible to companies' },
  { key: 'no', label: 'Hidden from companies' },
];

const PAGE_SIZE = 20;

export function AdminStudentsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [openToWork, setOpenToWork] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(status ? { statuses: status } : {}),
    ...graduationYearParam(graduationYear),
    ...(openToWork ? { open_to_opportunities: openToWork === 'yes' } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-students', params],
    queryFn: () => adminApi.listStudents(params),
    // Without this every keystroke unmounts the page for the loading state,
    // taking the search box and the focus in it -- so half of what was typed
    // silently becomes the filter.
    placeholderData: keepPreviousData,
  });

  /** Every filter change is a new result set, so page 1 is the only sane page. */
  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  const activeCount = [
    search !== '',
    status !== '',
    graduationYear !== '',
    openToWork !== '',
  ].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setGraduationYear('');
    setOpenToWork('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Students"
        description="Every student profile, with the university and graduation year to search by."
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Visibility to companies"
            tabs={LOOKING_TABS}
            value={openToWork}
            onChange={(key) => {
              setOpenToWork(key);
              setPage(1);
            }}
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Account"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={status}
              onChange={change(setStatus)}
            >
              <option value="">Any account status</option>
              <option value="ACTIVE">Active</option>
              <option value="PENDING_VERIFICATION">Pending verification</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="BANNED">Banned</option>
            </Select>
            <Input
              label="Graduating"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-32"
              type="number"
              placeholder="Graduating year"
              value={graduationYear}
              onChange={change(setGraduationYear)}
            />
            <SearchInput
              label="Search students"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Name, email or headline"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading students…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="No students"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No students yet"
                description="Student profiles appear here as students sign up."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => {
            const studying = [row.field_of_study, row.graduation_year].filter(Boolean).join(' · ');
            return (
              <BoxRow
                key={row.id}
                accent={ACCOUNT_TONES[row.account_status] ?? 'neutral'}
                leading={<BoxAvatar name={row.full_name} round />}
                title={row.full_name}
                to={paths.admin.studentDetail(row.id)}
                badges={
                  <>
                    <Badge dot tone={ACCOUNT_TONES[row.account_status] ?? 'neutral'}>
                      {humanizeStatus(row.account_status)}
                    </Badge>
                    {row.open_to_opportunities ? (
                      <Badge tone="brand">Visible to companies</Badge>
                    ) : (
                      <Badge tone="neutral">Hidden from companies</Badge>
                    )}
                  </>
                }
                subtitle={row.email}
                facts={[
                  { icon: 'university', text: row.university ?? 'University not given' },
                  row.university_email_verified && {
                    icon: 'shieldCheck',
                    text: 'Email verified',
                    tone: 'success',
                  },
                  {
                    icon: 'graduationCap',
                    text: [row.degree ?? 'No degree given', studying].filter(Boolean).join(' · '),
                  },
                  { icon: 'userCheck', text: `${row.profile_completion}% profile` },
                  {
                    icon: 'application',
                    text: `${row.application_count} ${
                      row.application_count === 1 ? 'application' : 'applications'
                    }`,
                  },
                  { icon: 'calendar', text: `Joined ${formatDate(row.created_at)}` },
                ]}
                actions={
                  <ViewButton
                    to={paths.admin.studentDetail(row.id)}
                    title={`Open ${row.full_name}`}
                  />
                }
              />
            );
          })}
        </BoxList>
      </ListCard>
    </div>
  );
}
